import { all, call, debounce, delay, fork, put, select, takeEvery, getContext } from 'redux-saga/effects';
import {
  AddPartnersBulkActionAPIPayload,
  AddTagsBulkActionAPIPayload,
  AssignImmigrationBulkActionAPIPayload,
  AssignStageBulkActionAPIPayload,
  BulkActionsAPIPayload,
  IIntakeIdWithDates,
  RemovePartnersBulkActionAPIPayload,
  RemoveTagsBulkActionAPIPayload,
  UpdateVisaSupportAction,
  Partners,
} from 'api-paths-cas';
import * as actions from './actions';
import * as dataActions from '../data/actions';
import { updateConnection } from '../ui/actions';
import { DashboardActionTypes } from './types';
import { DataActionTypes } from '../data/types';
import { logout } from '../auth/actions';
import { getAuth, getIsWsPaused, getUniId, getConfig, getIntakeId, getUI, getIntakeRange } from '../selectors';
import isTesting from '../../utilities/isTesting';
import * as api from '../../api/api';
import GetStudentsSaga, { createGetAllStudentsParams, getStudentSearchParams } from './sagas/handleGetAllStudents';
import GetApplicationsSaga from './sagas/handleGetAllApplications';
import GetSelectedPartnersSaga from './sagas/handleGetSelectedPartnersTotals';
import GetSelectedTagsSaga from './sagas/handleGetSelectedTagsTotals';
import GetFiltersSaga from './sagas/handleGetAllFilters';
import { getTags } from '../config/selectors';
import { getProgrammableTags } from '../programmable_tags/selectors';
import { AuthState } from '../auth/types';
import { BulkActionTypeEnum } from '../studentUsers/types';
import { ApplicationState } from '..';

function* handleStudentsPinTag(action: ReturnType<typeof actions.studentsPinTags>): any {
  try {
    const uni = yield select(getUniId);

    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentsPinTag');
      return;
    }

    yield call(api.studentsPinTag, idToken, uni, action.payload.studentsNumbers, action.payload.tags);

    const tags = yield select(getTags);
    const progTags = yield select(getProgrammableTags);
    const actionTagIds = action.payload.tags.map(tag => tag.id);
    const allTags = [...tags, ...progTags];
    const targetTags = allTags.filter(tag => actionTagIds.includes(tag.id));
    yield put(actions.studentsPinTagSuccess({ ...action.payload, tags: targetTags }));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsPinTagFailure(err.message));
    } else {
      yield put(actions.studentsPinTagFailure('An unknown error occured.'));
    }
  }
}

function* handleBulkAction(action: ReturnType<typeof actions.bulkAction>): any {
  try {
    const auth: AuthState = yield select(getAuth);
    const config = yield select(getConfig);
    const uni = yield select(getUniId);
    const intakeId: string = yield select(getIntakeId);
    const allSelected: boolean = yield select(({ dashboard }: ApplicationState) => dashboard.allSelected);
    const selectedRows: string[] = yield select(({ dashboard }: ApplicationState) => dashboard.selectedRows);
    if (!intakeId && !isTesting) {
      return;
    }
    const { headerTab } = yield select(getUI);
    const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId!);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleBulkAction');
      return;
    }
    const body = { data: action.payload.data } as BulkActionsAPIPayload;
    const filters = createGetAllStudentsParams({
      auth,
      config,
      intakeId,
      headerTab,
      intakeRange,
      idToken,
    }) as any;
    if (allSelected) {
      delete filters.abortController;
      delete filters.idToken;
      body.filters = filters;
    } else if (action.payload.studentNumbers) {
      body.studentIDs = action.payload.studentNumbers;
    } else if (selectedRows.length) {
      body.studentIDs = selectedRows;
    }

    switch (action.payload.bulkActionType) {
      case BulkActionTypeEnum.ADD_TAGS: {
        yield call(api.bulkPinTags, idToken, uni, body as AddTagsBulkActionAPIPayload);
        break;
      }
      case BulkActionTypeEnum.REMOVE_TAGS: {
        yield call(api.bulkRemoveTags, idToken, uni, body as RemoveTagsBulkActionAPIPayload);
        break;
      }
      case BulkActionTypeEnum.ASSIGN_STAGE: {
        yield call(api.bulkAssignStage, idToken, uni, body as AssignStageBulkActionAPIPayload);
        break;
      }
      case BulkActionTypeEnum.ADD_PARTNERS: {
        yield call(api.bulkAddPartners, idToken, uni, body as AddPartnersBulkActionAPIPayload);
        break;
      }
      case BulkActionTypeEnum.REMOVE_PARTNERS: {
        yield call(api.bulkRemovePartners, idToken, uni, body as RemovePartnersBulkActionAPIPayload);
        break;
      }
      case BulkActionTypeEnum.ASSIGN_IMMIGRATION_PROVIDER: {
        yield call(api.bulkAssignImmigrationProvider, idToken, uni, body as AssignImmigrationBulkActionAPIPayload);
        break;
      }
      default: {
        throw new Error('No bulk action type specified');
      }
    }
    yield put(actions.requestStudents(action.payload.isPartner));
    yield put(actions.requestTotals());

    yield put(actions.setSelectedRows([]));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.bulkActionFailure(err.message));
    } else {
      yield put(actions.bulkActionFailure(action.payload.bulkActionType as unknown as string));
    }
  }
}

function* handleStudentsUnpinTag(action: ReturnType<typeof actions.studentsUnpinTags>): any {
  try {
    const uni = yield select(getUniId);

    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentsUnpinTag');
      return;
    }

    yield call(api.studentsUnpinTags, idToken, uni, action.payload.studentsNumbers, action.payload.tags);
    const { payload } = action;

    yield put(actions.studentsUnpinTagSuccess({ ...payload }));

    // Keep sync with filtered data
    yield put(actions.requestStudents());
    yield put(actions.requestTotals());
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsUnpinTagFailure(err.message));
    } else {
      yield put(actions.studentsUnpinTagFailure('An unknown error occured.'));
    }
  }
}

function* handleStudentsAddImmigrationProvider(action: ReturnType<typeof actions.studentsAddImmigrationProvider>) {
  try {
    const uni = yield select(getUniId);

    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentsAddImmigrationProvider');
      return;
    }

    const response = yield call(
      api.studentsAddImmigrationProvider,
      idToken,
      uni,
      action.payload.studentsNumbers,
      action.payload.provider,
    );
    yield put(actions.studentsAddImmigrationProviderSuccess(response));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsAddImmigrationProviderFailure(err.message));
    } else {
      yield put(actions.studentsAddImmigrationProviderFailure('An unknown error occured.'));
    }
  }
}

function* handleStudentsRemoveImmigrationProvider(
  action: ReturnType<typeof actions.studentsRemoveImmigrationProvider>,
) {
  try {
    const uni = yield select(getUniId);

    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentsRemoveImmigrationProvider');
      return;
    }

    const response = yield call(api.studentsRemoveImmigrationProvider, idToken, uni, action.payload.studentsNumbers);

    yield put(actions.studentsRemoveImmigrationProviderSuccess(response));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsRemoveImmigrationProviderFailure(err.message));
    } else {
      yield put(actions.studentsRemoveImmigrationProviderFailure('An unknown error occured.'));
    }
  }
}

function* handleStudentsAddServices(action: ReturnType<typeof actions.studentsAddServices>): any {
  try {
    const uni = yield select(getUniId);

    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentsAddServices');
      return;
    }

    const response = yield call(
      api.studentsAddServices,
      idToken,
      uni,
      action.payload.studentsNumbers,
      action.payload.services,
    );
    yield put(actions.studentsAddServicesSuccess(response));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsAddServicesFailure(err.message));
    } else {
      yield put(actions.studentsAddServicesFailure('An unknown error occured.'));
    }
  }
}

function* handleStudentsRemoveServices(action: ReturnType<typeof actions.studentsRemoveServices>): any {
  try {
    const uni = yield select(getUniId);

    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentsRemoveServices');
      return;
    }

    const response = yield call(
      api.studentsRemoveServices,
      idToken,
      uni,
      action.payload.studentsNumbers,
      action.payload.services,
    );

    yield put(actions.studentsRemoveServicesSuccess(response));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentsRemoveServicesFailure(err.message));
    } else {
      yield put(actions.studentsRemoveServicesFailure('An unknown error occured.'));
    }
  }
}

function* handleStudentAssignVisaSupport(action: ReturnType<typeof actions.studentAssignVisaSupport>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentAssignVisaSupport');
      return;
    }
    const { uni, studentNo, intakeId, offerId } = action.payload;
    const response = yield call(
      api.updateVisaSupport,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      UpdateVisaSupportAction.ASSIGN,
    );
    yield put(actions.studentAssignVisaSupportSuccess(response));
    yield put(
      actions.bulkAction(
        BulkActionTypeEnum.ADD_PARTNERS,
        [Partners.ENROLY_VT],
        [`${uni}#${studentNo}#${intakeId}#${offerId}`],
        true,
      ),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentAssignVisaSupportFailure(err.message));
    } else {
      yield put(actions.studentAssignVisaSupportFailure('An unknown error occured.'));
    }
  }
}

function* handleStudentUnassignVisaSupport(action: ReturnType<typeof actions.studentUnassignVisaSupport>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleStudentUnassignVisaSupport');
      return;
    }
    const { uni, studentNo, intakeId, offerId } = action.payload;
    const response = yield call(
      api.updateVisaSupport,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      UpdateVisaSupportAction.UNASSIGN,
    );
    yield put(actions.studentUnassignVisaSupportSuccess(response));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.studentUnassignVisaSupportFailure(err.message));
    } else {
      yield put(actions.studentUnassignVisaSupportFailure('An unknown error occured.'));
    }
  }
}

function* handleRequestScheduledInterview(action: ReturnType<typeof actions.requestScheduledInterview>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRequestScheduledInterview');
      return;
    }

    const { uni, students, eventType } = action.payload;

    const allSelected: boolean = yield select(({ dashboard }: ApplicationState) => dashboard.allSelected);
    let response: object;
    if (allSelected) {
      const filters: api.IGetAllParams = yield call(getStudentSearchParams);
      response = yield call(
        api.toggleScheduledInterviewStatusBulk,
        idToken,
        uni,
        { filters, data: [eventType], note: '' },
        'add',
      );
    } else {
      response = yield call(
        api.toggleScheduledInterviewStatusBulk,
        idToken,
        uni,
        { studentIDs: students, data: [eventType], note: '' },
        'add',
      );
    }
    yield put(actions.requestScheduledInterviewSuccess(response));
    // Removed requestStudents() call - rely on optimistic update + WebSocket for UI consistency
    yield put(actions.selectAll(false)); // uncheck allSelected
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.requestScheduledInterviewFailure(err.message));
    } else {
      yield put(actions.requestScheduledInterviewFailure('An unknown error occurred.'));
    }
  }
}

function* handleCancelScheduledInterview(action: ReturnType<typeof actions.cancelScheduledInterview>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCancelScheduledInterview');
      return;
    }
    const { uni, studentNo, eventTypeId, eventKey } = action.payload;

    const response: object = yield call(
      api.toggleScheduledInterviewStatus,
      idToken,
      uni,
      studentNo,
      { eventType: eventTypeId, eventKey, note: '' },
      'cancel',
    );
    yield put(actions.cancelScheduledInterviewSuccess(response));
    // Removed requestStudents() call - rely on optimistic update + WebSocket for UI consistency
    yield put(actions.selectAll(false)); // uncheck allSelected
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.requestScheduledInterviewFailure(err.message));
    } else {
      yield put(actions.requestScheduledInterviewFailure('An unknown error occurred.'));
    }
  }
}

function* handleCancelScheduledInterviewBulk(action: ReturnType<typeof actions.cancelScheduledInterviewBulk>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCancelScheduledInterviewBulk');
      return;
    }

    const { uni, students, eventType } = action.payload;

    const allSelected: boolean = yield select(({ dashboard }: ApplicationState) => dashboard.allSelected);
    let response: object;
    if (allSelected) {
      const filters: api.IGetAllParams = yield call(getStudentSearchParams);
      response = yield call(
        api.toggleScheduledInterviewStatusBulk,
        idToken,
        uni,
        { filters, data: eventType.id, note: '' },
        'remove',
      );
    } else {
      response = yield call(
        api.toggleScheduledInterviewStatusBulk,
        idToken,
        uni,
        { studentIDs: students, data: eventType.id, note: '' },
        'remove',
      );
    }
    yield put(actions.cancelScheduledInterviewBulkSuccess(response));
    // Removed requestStudents() call - rely on optimistic update + WebSocket for UI consistency
    yield put(actions.selectAll(false)); // uncheck allSelected
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.cancelScheduledInterviewBulkFailure(err.message));
    } else {
      yield put(actions.cancelScheduledInterviewBulkFailure('An unknown error occurred.'));
    }
  }
}

function* handleCancelRecordedInterviewBulk(action: ReturnType<typeof actions.cancelRecordedInterviewBulk>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCancelRecordedInterviewBulk');
      return;
    }

    const { uni, students, regionInterviewConfig } = action.payload;
    const allSelected: boolean = yield select(({ dashboard }: ApplicationState) => dashboard.allSelected);
    let response: object;
    if (allSelected) {
      const filters: api.IGetAllParams = yield call(getStudentSearchParams);
      response = yield call(api.cancelRecordedInterviewBulk, idToken, uni, regionInterviewConfig, undefined, filters);
    } else {
      response = yield call(api.cancelRecordedInterviewBulk, idToken, uni, regionInterviewConfig, students);
    }
    yield put(actions.cancelRecordedInterviewBulkSuccess(response));
    // Removed requestStudents() call - rely on optimistic update + WebSocket for UI consistency
    yield put(actions.selectAll(false)); // uncheck allSelected
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.cancelRecordedInterviewBulkFailure(err.message));
    } else {
      yield put(actions.cancelRecordedInterviewBulkFailure('An unknown error occurred.'));
    }
  }
}

function* handleCancelRecordedInterview(action: ReturnType<typeof actions.cancelRecordedInterview>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCancelRecordedInterview');
      return;
    }

    const { uni, studentNo, intakeId, offerId, interviewHash } = action.payload;

    const response: object = yield call(
      api.cancelRecordedInterview as any,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      interviewHash,
    );
    yield put(actions.cancelRecordedInterviewSuccess(response));
    // Removed requestStudents() call - rely on optimistic update + WebSocket for UI consistency
    yield put(actions.selectAll(false)); // uncheck allSelected
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.cancelRecordedInterviewFailure(err.message));
    } else {
      yield put(actions.cancelRecordedInterviewFailure('An unknown error occurred.'));
    }
  }
}

function* handleRequestRecordedInterview(action: ReturnType<typeof actions.onRequestRecordedInterview>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRequestRecordedInterview');
      return;
    }

    const { uni, students, regionInterviewConfig } = action.payload;
    const allSelected: boolean = yield select(({ dashboard }: ApplicationState) => dashboard.allSelected);

    if (allSelected) {
      const filters: api.IGetAllParams = yield call(getStudentSearchParams);
      const { offer } = yield call(
        api.bulkRequestStudentRecordedInterview,
        idToken,
        uni,
        regionInterviewConfig,
        undefined,
        filters,
      );
      yield put(actions.onRequestRecordedInterviewSuccess(offer));
      yield put(actions.requestStudents());
      yield put(actions.selectAll(false)); // uncheck allSelected
    } else {
      const { offer } = yield call(
        api.bulkRequestStudentRecordedInterview,
        idToken,
        uni,
        regionInterviewConfig,
        students,
      );
      yield put(actions.onRequestRecordedInterviewSuccess(offer));
      yield put(actions.requestStudents());
      yield put(actions.selectAll(false)); // uncheck allSelected
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.onRequestRecordedInterviewFailure(err.message));
    } else {
      yield put(actions.onRequestRecordedInterviewFailure('An unknown error occurred.'));
    }
  }
}

function* handleDebouncedMessage(action: ReturnType<typeof dataActions.debounceWsMessage>) {
  yield put(dataActions.resendWSMessage(action.payload));
}

function* handleMessage(action: any) {
  const isWSPaused = yield select(getIsWsPaused);

  if (isWSPaused) {
    yield delay(1000);
    return yield put(dataActions.debounceWsMessage(action.payload));
  }
  const auth = yield select(getAuth);
  const config = yield select(getConfig);
  const message = JSON.parse(action.payload.message);
  switch (message.type) {
    case 'UpdateStudent':
      yield put(actions.updateStudent(message.payload, auth, config));
      break;
    case 'student-opened':
      yield put(actions.studentOpenedReceived(message.payload));
      break;
    case 'student-closed':
      yield put(actions.studentClosedReceived(message.payload));
      break;

    default:
      break;
  }
}

function* handleConnected() {
  yield put(updateConnection(true));
}

function* handleMultiStudentTransition(action: any) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleMultiStudentTransition');
      return;
    }

    yield call(api.bulkChange, idToken, action.payload.studentIDs, action.payload.status, 'status');
    yield put(actions.multiTransitionStatusSuccess(action.payload));
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      yield put(actions.multiTransitionStatusFailure(err.message));
    } else {
      yield put(actions.multiTransitionStatusFailure('An unknown error occured.'));
    }
  }
}

function* watchPinTag() {
  yield takeEvery(DashboardActionTypes.STUDENTS_TAGS_PIN, handleStudentsPinTag);
}

function* watchAllStudentsBulkActions() {
  yield takeEvery(DashboardActionTypes.BULK_ACTION, handleBulkAction);
}

function* watchUnpinTag() {
  yield takeEvery(DashboardActionTypes.STUDENTS_TAGS_UNPIN, handleStudentsUnpinTag);
}

function* watchAddImmigrationProvider() {
  yield takeEvery(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_ADD, handleStudentsAddImmigrationProvider);
}

function* watchRemoveImmigrationProvider() {
  yield takeEvery(DashboardActionTypes.STUDENTS_IMMIGRATION_PROVIDER_REMOVE, handleStudentsRemoveImmigrationProvider);
}

function* watchAddService() {
  yield takeEvery(DashboardActionTypes.STUDENTS_SERVICES_ADD, handleStudentsAddServices);
}

function* watchRemoveServices() {
  yield takeEvery(DashboardActionTypes.STUDENTS_SERVICES_REMOVE, handleStudentsRemoveServices);
}

function* watchMultiStudentTransition() {
  yield takeEvery(DashboardActionTypes.MULTI_TRANSITION_STATUS, handleMultiStudentTransition);
}

function* watchAssignVisaSupport() {
  yield takeEvery(DashboardActionTypes.STUDENT_ASSIGN_VISA_SUPPORT, handleStudentAssignVisaSupport);
}

function* watchUnassignVisaSupport() {
  yield takeEvery(DashboardActionTypes.STUDENT_UNASSIGN_VISA_SUPPORT, handleStudentUnassignVisaSupport);
}

function* watchRequestInterview() {
  yield takeEvery(DashboardActionTypes.REQUEST_SCHEDULED_INTERVIEW, handleRequestScheduledInterview);
}

function* watchCancelInterviewBulk() {
  yield takeEvery(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW_BULK, handleCancelScheduledInterviewBulk);
}

function* watchCancelInterview() {
  yield takeEvery(DashboardActionTypes.CANCEL_SCHEDULED_INTERVIEW, handleCancelScheduledInterview);
}

function* watchRequestRecordedInterview() {
  yield takeEvery(DashboardActionTypes.REQUEST_RECORDED_INTERVIEW_BULK, handleRequestRecordedInterview);
}

function* watchWSConnect() {
  yield takeEvery('REDUX_WEBSOCKET::OPEN', handleConnected);
}

function* watchWSMessage() {
  yield takeEvery('REDUX_WEBSOCKET::MESSAGE', handleMessage);
}

function* watchDebouncedWSEvents() {
  yield debounce(1000, DataActionTypes.DEBOUNCE_WS_ACTION, handleDebouncedMessage);
}

function* watchCancelRecordedInterviewBulk() {
  yield takeEvery(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW_BULK, handleCancelRecordedInterviewBulk);
}

function* watchCancelRecordedInterview() {
  yield takeEvery(DashboardActionTypes.CANCEL_RECORDED_INTERVIEW, handleCancelRecordedInterview);
}

// unused function - commented out to fix TS6133
/*
function* handleTiggerSITSTransfer(action: ReturnType<typeof actions.triggerSITSTransfer>): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleTiggerSITSTransfer');
      return;
    }
    const config = yield select(getConfig);
    const studentNumber = action.payload;

    const res = yield call(api.triggerSITSTransfer, idToken, config.university_code, studentNumber);
    yield put(actions.triggerSITSTransferSuccess(JSON.stringify(res)));
  } catch (err) {
    // Error message not used, directly call failure action
    yield put(actions.triggerSITSTransferFailure());
  }
}
*/

function* DashboardSaga() {
  // Fixed: Remove duplicate fork calls to prevent memory leaks
  yield all([
    fork(watchPinTag),
    fork(watchUnpinTag),
    fork(watchAddImmigrationProvider),
    fork(watchRemoveImmigrationProvider),
    fork(watchDebouncedWSEvents),
    fork(watchWSConnect),
    fork(watchWSMessage),
    fork(watchAllStudentsBulkActions),
    fork(watchAddService),
    fork(watchRemoveServices),
    fork(watchMultiStudentTransition),
    fork(watchAssignVisaSupport),
    fork(watchRequestInterview),
    fork(watchCancelInterviewBulk),
    fork(watchCancelInterview),
    fork(watchCancelRecordedInterviewBulk),
    fork(watchCancelRecordedInterview),
    fork(watchRequestRecordedInterview),
    fork(watchUnassignVisaSupport),
    fork(GetStudentsSaga),
    fork(GetApplicationsSaga),
    fork(GetFiltersSaga),
    fork(GetSelectedPartnersSaga),
    fork(GetSelectedTagsSaga),
  ]);
}

export default DashboardSaga;
